<template>
  <BlokkliProvider v-bind="node.blokkliProps" class="mb-64 md:mb-112">
    <ProductFinderStage
      :overline="node.overline || ''"
      :headline="node.headline || ''"
      :image="node.mainImage"
    />
    <div class="layout-grid pt-40">
      <div
        class="col-span-4 col-start-2 md:col-span-6 md:col-start-2 xxxl:mx-[-264px]"
      >
        <h3
          class="typo-headline-5 mb-8 uppercase"
          :class="{
            'font-bold': isRebranding,
          }"
        >
          {{ $texts('productFinder.allStraps', 'All straps') }}
        </h3>
        <div class="text-grey-dark-02 md:col-span-6 md:text-lg">
          {{
            $texts(
              'productFinder.searchText',
              'To find the perfect strap for your watch, simply enter the SKU number in the search box.',
            )
          }}
          <button class="group relative inline-block underline duration-300">
            {{
              $texts(
                'productFinder.tooltipButton',
                'Here you can find the number',
              )
            }}
            <span
              class="-left-5 absolute -top-8 hidden w-full max-w-[320px] -translate-y-full bg-grey-dark-01 p-16 text-base text-white after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-8 after:border-grey-dark-01 after:border-x-transparent after:border-b-transparent after:content-[''] group-hover:flex group-focus:flex"
            >
              {{
                $texts(
                  'productFinder.tooltipText',
                  'SKU No° is the number your watch is refferenced to. You can found her on the back of your watch, receipt or the packaging',
                )
              }}
            </span>
          </button>
        </div>
      </div>
    </div>
    <ProductFinderBar
      v-model="searchText"
      :selected-facets="selectedFacets"
      :mode="mode"
    />
    <ProductFinderResults :products="results" strap :final="!loadMoreLink" />
    <ProductFinderLoadMore v-if="loadMoreLink" :to="loadMoreLink" />
    <BlokkliField name="field_paragraphs" :list="node.paragraphs" />
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import { type NodePageFragment } from '#graphql-operations'

definePageMeta({
  name: 'strap-finder',
  staticDrupalPage: 'strap_finder',
})

const { $texts } = useEasyTexts()
const isRebranding = await useFeatureFlag()

const route = useRoute()

const loadRouteQuery = async () => {
  // + at the end of route.fullPath leads to Drupal crashing and we don't need it for the metatags.
  const trimmedPath = route.fullPath.replace(/\++$/, '')

  const routeQuery = await useCachedGraphqlQuery('route', {
    path: trimmedPath,
  })

  return routeQuery.data
}

const { data: query } = await useAsyncData(route.fullPath, loadRouteQuery, {
  watch: [route],
})

const { entity: node } = await useDrupalRoute<NodePageFragment>(query)

const { results, loadMoreLink, searchText, selectedFacets, mode } =
  await useProductFinder('straps')

useRouteCache((ctx) => ctx.setMaxAge(15 * 60).setCacheable())

const { trackPageView } = useAnalyticsTracking()
trackPageView()
</script>

<style lang="scss" scoped>
:deep(.paragraph),
.paragraph {
  @include paragraph-spacing;
}
</style>
